// Progressive Disclosure - Revealing information gradually
export class ProgressiveDisclosure {
  private static steps = ['email', 'questions', 'complete'];

  static getCurrentStep(completedSteps: string[]): string {
    if (completedSteps.length === 0) return 'email';
    if (completedSteps.includes('questions')) return 'questions';
    return 'complete';
  }

  static getProgressPercentage(completedSteps: string[]): number {
    if (completedSteps.length === 0) return 0;
    if (completedSteps.includes('email') && completedSteps.includes('questions')) return 100;
    if (completedSteps.includes('email')) return 33.33;
    return 0;
  }

  static shouldShowStep(stepId: string, completedSteps: string[]): boolean {
    const stepIndex = this.steps.indexOf(stepId);
    const lastCompletedIndex = Math.max(...completedSteps.map(s => this.steps.indexOf(s)), -1);
    return stepIndex <= lastCompletedIndex + 1;
  }
}

// Exit Intent Detection - Trigger popup when user is about to leave
export class ExitIntentDetector {
  private static isActive = false;
  private static callback: (() => void) | null = null;
  private static lastTriggerTime = 0;
  private static cooldownPeriod = 30000; // 30 seconds

  static startDetection(callback: () => void): () => void {
    if (this.isActive) return this.stopDetection;

    this.callback = callback;
    this.isActive = true;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && this.shouldTrigger()) {
        this.trigger();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab' && this.shouldTrigger()) {
        this.trigger();
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('keydown', handleKeyDown);
      this.stopDetection();
    };
  }

  static stopDetection(): void {
    this.isActive = false;
    this.callback = null;
  }

  static reset(): void {
    this.lastTriggerTime = Date.now();
  }

  private static shouldTrigger(): boolean {
    return Date.now() - this.lastTriggerTime > this.cooldownPeriod;
  }

  private static trigger(): void {
    if (this.callback && this.shouldTrigger()) {
      this.lastTriggerTime = Date.now();
      this.callback();
    }
  }
}

// Commitment Escalation - Guiding users through small commitments
export class CommitmentEscalation {
  private static commitments: string[] = [];

  static addCommitment(commitment: string): void {
    this.commitments.push(commitment);
  }

  static getCommitmentLevel(): number {
    return this.commitments.length;
  }

  static shouldEscalate(): boolean {
    return this.commitments.length >= 2;
  }

  static getNextCommitment(): string | null {
    const commitmentOptions = [
      'Share with a friend',
      'Join our Discord community',
      'Follow us on social media',
      'Subscribe to our newsletter'
    ];

    const usedCommitments = new Set(this.commitments);
    const availableCommitments = commitmentOptions.filter(c => !usedCommitments.has(c));

    return availableCommitments[0] || null;
  }
}

// Default Bias - Pre-selecting options to guide user choices
export class DefaultBias {
  static getDefaultKeepUpdated(): boolean {
    return true; // Most users want updates
  }

  static getDefaultRole(): string {
    return 'Student'; // Common default
  }

  static getDefaultWatchLaterSize(): string {
    return '20-100 videos'; // Middle range
  }

  static getDefaultPrimaryUseCase(): string {
    return 'Personal learning'; // Broad appeal
  }
}

// Form Validation - Client-side validation with helpful messages
export class FormValidation {
  static getEmailError(email: string): string | null {
    if (!email) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return 'Please enter a valid email address';
    }
    return null;
  }

  static getRequiredFieldError(value: string, fieldName: string): string | null {
    if (!value || value.trim() === '') {
      return `${fieldName} is required`;
    }
    return null;
  }

  static getMinLengthError(value: string, minLength: number, fieldName: string): string | null {
    if (value && value.length < minLength) {
      return `${fieldName} must be at least ${minLength} characters`;
    }
    return null;
  }

  static getMaxLengthError(value: string, maxLength: number, fieldName: string): string | null {
    if (value && value.length > maxLength) {
      return `${fieldName} must be no more than ${maxLength} characters`;
    }
    return null;
  }
}
